import React from 'react'
import Link from 'next/link'
import Image from 'next/image'

import Header from '../components/public/shared/Header'
import Footer from '../components/public/shared/Footer'
import { isAuthenticated } from '../utils/auth'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faLinkedinIn } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconLookup } from '@fortawesome/fontawesome-common-types'
import headerImg from '../public/static/img/about.jpg'
import LogoList, { LogoProps } from 'components/shared/LogoList'

const icon: IconLookup = {
	prefix: 'fab',
	iconName: 'linkedin-in',
}

library.add({ icon: faLinkedinIn.icon, prefix: 'fab', iconName: 'linkedin-in' })

const basePath = process.env.BASE_PATH

interface AboutUsProps {
	authenticated: boolean
}

export const getServerSideProps = (ctx) => {
	return {
		props: {
			authenticated: isAuthenticated(ctx),
			title: 'About us',
			description:
				'We are on a mission to bridge the gap between university and industry.',
			url: `${basePath}/about-us`,
			image: 'master_challenge_about_us.png',
		},
	}
}

const teamMembers = [
	{
		name: 'Bram Kuijken',
		function: 'Chief Education Officer',
		linkedin: 'https://www.linkedin.com/in/bramkuijken/',
		phone: '+316 220 906 99',
		img: '/static/img/bram.jpeg',
	},
	{
		name: 'Ruben Kuipers',
		function: 'Designer & Front-end developer',
		linkedin: 'https://www.linkedin.com/in/rubenkuipers/',
		img: '/static/img/ruben.jpg',
	},
	{
		name: 'Peter Otto',
		function: 'Back-end developer',
		linkedin: 'https://www.linkedin.com/in/peter-otto-91164250/',
		img: '/static/img/peter.jpeg',
	},
	{
		name: 'Robin Breed',
		function: 'Business Developer',
		linkedin: 'https://www.linkedin.com/in/robinbreed1992/',
		img: '/static/img/robin.jpeg',
	},
	{
		name: 'Gioia Lelli',
		function: 'Business Developer',
		linkedin: 'https://www.linkedin.com/in/gioia-lelli-a83776150/',
		img: '/static/img/Gioia.jpg',
	},
	{
		name: 'Tim Krombholz',
		function: 'Business developer',
		linkedin: 'https://www.linkedin.com/in/timkrombholz/',
		img: '/static/img/tim.jpeg',
	},
]

// Port in to using useState hooks, if you need state
class AboutUs extends React.Component<AboutUsProps> {
	constructor(props: any) {
		super(props)
	}

	render() {
		const { authenticated } = this.props

		const logos: LogoProps[] = [
			{
				src: '/static/img/vu-logo.png',
				alt: 'Vrije Universiteit Amsterdam',
				maxWidth: '8rem',
				maxHeight: '6rem',
			},
			{
				src: '/static/img/uva-logo.png',
				alt: 'Universiteit van Amsterdam',
				maxWidth: '8.5rem',
				maxHeight: '6rem',
			},
			{
				src: '/static/img/tilburg-university-logo.png',
				alt: 'Univesiteit van Tilburg',
				maxWidth: '10rem',
				maxHeight: '6rem',
			},
			{
				src: '/static/img/UU-logo.png',
				alt: 'Universiteit Utrecht',
				maxWidth: '10rem',
				maxHeight: '6rem',
			},
			{
				src: '/static/img/nyenrode_logo.png',
				alt: 'Nyenrode Business Universiteit',
				maxWidth: '9rem',
				maxHeight: '6rem',
			},
			{
				src: '/static/img/uni-leiden.png',
				alt: 'Universiteit Leiden',
				maxWidth: '9rem',
				maxHeight: '6rem',
			},
			{
				src: '/static/img/hu_logo.png',
				alt: 'Hogeschool Utrecht',
				maxWidth: '8rem',
				maxHeight: '6rem',
			},
		]

		return (
			<div className="wrapper">
				<Header authenticated={authenticated} />
				<main className="pt-20 relative z-20">
					<div className="relative h-full z-20 bg-gray-900">
						<div className="container mx-auto px-4 pt-8 md:pt-20 pb-32 relative z-20">
							<div className="w-full md:w-1/2 mt-4">
								<h1 className="text-3xl md:text-5xl mb-4 font-sans font-bold text-white">
									On a mission to bridge the gap{' '}
									<span className="text-yellow-300">
										between university and industry
									</span>
								</h1>
								<p className="text-lg md:text-xl mb-4 font-sans font-normal text-white opacity-75">
									Master Challenge aims to further close the gap between
									university and industry. We allow university lecturers to
									easily integrate real-life company challenges into their
									courses.
								</p>
							</div>
						</div>
						<div className="bg-gradient-to-b md:bg-gradient-to-r z-10 absolute from-gray-900 h-1/2 md:h-full right-0 bottom-0 top-auto md:top-0 w-full md:w-7/12"></div>
						<div className="absolute right-0 bottom-0 top-auto md:top-0 h-1/2 md:h-full w-full md:w-7/12">
							<Image
								layout="fill"
								objectFit="cover"
								objectPosition="center"
								src={headerImg}
								alt="For students"
							/>
						</div>
					</div>

					<LogoList title="Affiliated educational institutions" logos={logos} />

					<div className="relative pt-16 pb-10 border-t bg-gradient-to-b from-gray-50">
						<div className="container mx-auto px-4 relative text-center leading-relaxed">
							<h2 className="text-3xl mb-4 font-sans font-bold text-gray-900">
								Real-life challenges solved by students
							</h2>
							<p className="mb-4 text-gray-700 mx-auto max-w-3xl text-lg">
								We experienced that students enjoy working on real-life
								challenges of companies. Next to that, companies appreciate it
								that their (short term) challenges are being solved while
								getting in contact with talented young people. That’s why we
								started Master Challenge. A hassle-free platform for educators
								to find real-life company challenges that can be easily matched
								to their students.
							</p>
						</div>
					</div>

					{teamMembers.length > 0 && (
						<div className="relative z-10 bg-white border-t">
							<div className="container mx-auto lg:max-w-5xl px-4 md:px-8 relative">
								<div className="relative  mx-auto leading-relaxed py-8">
									<h3 className="text-3xl text-center mb-4 font-sans font-bold">
										Our team
									</h3>

									<ul className="flex flex-wrap justify-center -mx-6 text-center">
										{teamMembers.map((member, index) => {
											return (
												<li className="p-6 w-1/2 md:w-1/3" key={index}>
													<div className="relative inline-block mb-3">
														<div className="rounded-full relative w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 overflow-hidden">
															<Image
																src={member.img}
																layout="fill"
																objectFit="cover"
																className="object-cover block w-full h-full"
																alt={member.name}
															/>
														</div>
														<a
															href={member.linkedin}
															target="_blank"
															rel="noreferrer"
															className="bg-yellow-300 text-black rounded-full flex items-center justify-center w-8 h-8 md:w-10 md:h-10 hover:bg-yellow-200 duration-300 transition-colors absolute right-0 bottom-0 z-10"
														>
															<FontAwesomeIcon icon={icon} />
														</a>
													</div>

													<h4 className="font-bold text-xl">{member.name}</h4>
													<span className="block">{member.function}</span>
													{member.phone && (
														<a href={`tel:${member.phone}`} className="block">
															{member.phone}
														</a>
													)}
												</li>
											)
										})}
									</ul>
								</div>
							</div>
						</div>
					)}

					<div className="relative border-t bg-gradient-to-b from-gray-50">
						<div className="container mx-auto px-4 pb-16 pt-6 relative z-10">
							<div className="flex flex-wrap items-center justify-center py-10">
								<h2 className="font-sans font-bold text-center w-full text-black text-2xl md:mr-10 mb-2 md:w-auto md:text-left">
									Join us!
								</h2>
								<Link href="/join-us" passHref>
									<button className="bg-yellow-300 border border-yellow-300 text-black hover:bg-yellow-200 hover:border-yellow-200 transition-colors duration-300 mr-4 my-2">
										View our open positions
									</button>
								</Link>
							</div>
						</div>
					</div>
				</main>
				<Footer />
			</div>
		)
	}
}

export default AboutUs
